import { useCallback, useMemo } from 'react';

import { useMenuExperimentVariant } from 'experiments/menu-variant/use-menu-experiment-variant-name';
import { useOneMenuQuery } from 'generated/graphql-gateway';
import { DEFAULT_MENU_VARIANT_KEY } from 'remote/constants';
import { IIngredients } from 'state/unified-menu/types';

export type OneMenuCatalogEntry = NonNullable<
  NonNullable<ReturnType<typeof useOneMenuQuery>['data']>['OneMenu']
>['entries'][number];

/**
 * Store-less OneMenu catalog for menu-item deeplink preview.
 * Maps entries by id; does not drive UnifiedMenu indexing.
 */
export function useOneMenuCatalog({ skip = false }: { skip?: boolean } = {}) {
  const menuExperimentVariant = useMenuExperimentVariant();
  const isMenuExperimentPending = menuExperimentVariant?.isPending === true;
  const amplitudeVariantKey =
    menuExperimentVariant && !menuExperimentVariant.isPending
      ? menuExperimentVariant.variantName
      : undefined;
  const variantName = amplitudeVariantKey || DEFAULT_MENU_VARIANT_KEY;

  const { data, loading, error } = useOneMenuQuery({
    variables: { variantName },
    // Match UnifiedMenu: do not fetch while Amplitude menu assignment is pending.
    skip: skip || isMenuExperimentPending,
  });

  const entriesById = useMemo(() => {
    const map = new Map<string, OneMenuCatalogEntry>();
    const entries = data?.OneMenu?.entries;
    if (!entries) {
      return map;
    }
    for (const entry of entries) {
      map.set(entry.id, entry);
    }
    return map;
  }, [data?.OneMenu?.entries]);

  const findEntry = useCallback((id: string) => entriesById.get(id), [entriesById]);

  const ingredients = useMemo(
    () => (data?.OneMenu?.ingredients as IIngredients[] | undefined) ?? [],
    [data?.OneMenu?.ingredients]
  );

  return {
    entriesById,
    findEntry,
    loading: loading || isMenuExperimentPending,
    error,
    rootMenuEntryId: data?.OneMenu?.rootMenuEntryId,
    imageBaseUrl: data?.OneMenu?.meta?.imageBaseUrl,
    ingredients,
    entries: data?.OneMenu?.entries ?? [],
  };
}
